import React, { useRef, useEffect, useState } from 'react';
import { FishSymbol, Gift, ChevronRight, ArrowRight } from 'lucide-react';
import GlobalHeader from './GlobalHeader';
import CycleStatusWidget from './CycleStatusWidget';
import StoriesHighlights from './StoriesHighlights';
import FAQAccordion from './FAQAccordion';
import ProductCardHybrid from './ProductCardHybrid';
import '../styles/HomePage.css';
import '../styles/ProductCardHybrid.css';
import '../styles/HorizontalScrollSection.css';

const logoUrl = new URL('../assets/icons/fish-logo.png', import.meta.url).href;

const HomePage = ({
  products = [],
  stories = [],
  onNavigateToCart,
  onNavigateToCatalog,
  progress = { percent: 0, families: 0 },
  // Данные глобального цикла (даты, тема, статус)
  cycleInfo,
  // Для desktop навигации в GlobalHeader
  currentPage = 'home',
  onNavigate,
  cartItemsCount = 0,
  // Для добавления в корзину
  onAddToCart,
  // Для управления количеством
  onUpdateQuantity,
  onRemoveFromCart,
  cartItems = [],
  // Для просмотра деталей товара
  onViewDetails,
  // Гостевой режим
  isAuthenticated = true
}) => {
  // Ref для sentinel-элемента внутри виджета (между header и timeline)
  const timerSentinelRef = useRef(null);
  // Ref для горизонтального скролла
  const scrollRef = useRef(null);
  // Состояние видимости таймера в виджете
  const [isWidgetTimerVisible, setIsWidgetTimerVisible] = useState(true);

  // Scroll listener: показываем таймер в хедере когда sentinel ушёл за sticky header
  useEffect(() => {
    if (!timerSentinelRef.current) return;

    let rafId;
    const checkPosition = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (!timerSentinelRef.current) return;
        const top = timerSentinelRef.current.getBoundingClientRect().top;
        // sentinel ещё на экране (top > 0) → виджет-таймер видно → скрыть header-таймер
        // sentinel ушёл за верх экрана (top <= 0) → виджет-таймер не видно → показать header-таймер
        setIsWidgetTimerVisible(top > 0);
      });
    };

    window.addEventListener('scroll', checkPosition, { passive: true });
    checkPosition(); // начальная проверка

    return () => {
      window.removeEventListener('scroll', checkPosition);
      cancelAnimationFrame(rafId);
    };
  }, []);
  // "Сейчас заказывают" - умная логика:
  // 1. Если есть orders_count > 0 → сортируем по популярности в текущем цикле
  // 2. Если нет заказов (все 0) → показываем рандомные 6 товаров
  const getCurrentlyOrderedProducts = () => {
    const hasRealOrders = products.some(p => (p.orders_count || 0) > 0);

    if (hasRealOrders) {
      // Сортируем по реальной популярности в текущем цикле
      return [...products]
        .sort((a, b) => (b.orders_count || 0) - (a.orders_count || 0))
        .slice(0, 6);
    } else {
      // Нет заказов → рандомная выборка (создаём иллюзию активности)
      const shuffled = [...products].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, 6);
    }
  };

  const currentlyOrderedProducts = getCurrentlyOrderedProducts();

  const handleAddToCartClick = (product, weight) => {
    if (onAddToCart) {
      onAddToCart(product, weight?.value || 1);
      // Haptic feedback
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
      }
    }
  };

  // Просто передаём вызов родителю (App.jsx)
  const handleViewDetails = (product) => {
    if (onViewDetails) {
      onViewDetails(product);
    }
  };

  // Преобразуем данные из API в формат ProductCardHybrid
  // Сохраняем ВСЕ поля для ProductDetailsModal (spread оператор + явные маппинги)
  const transformProduct = (product) => ({
    ...product, // Сохраняем все оригинальные поля (wholesale_pack_size, wholesale_bonus_percent и др.)
    id: product.product_id,
    name: product.product_name,
    shortDescription: product.description || '',
    imageUrl: product.image_url || null,
    basePricePerKg: parseFloat(product.price || 0),
    oldPricePerKg: product.old_price ? parseFloat(product.old_price) : null,
    packagingBonus: product.packaging_bonus || 0,
    buyersCount: product.orders_count ?? 0,
    buyersInitials: product.buyer_initials || [],
    badge: product.badge || null,
    weights: product.unit ? [{ value: 1, label: `1 ${product.unit}`, isWholesale: false }] : null
  });

  // Дата закрытия: из API или фолбек +7 дней (чтобы таймер всегда был виден)
  const closeDate = cycleInfo?.end_date
    ? new Date(cycleInfo.end_date)
    : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return (
    <div className="home-page">
      <GlobalHeader
        location="Москва и МО"
        currentPage={currentPage}
        onNavigate={onNavigate}
        cartItemsCount={cartItemsCount}
        showTimer={!isWidgetTimerVisible}
        closeDate={closeDate}
      />

      {/* Hero Banner — показывается ВСЕМ */}
      <section className="hero-banner relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 pt-12 pb-16 md:pt-20 md:pb-24">
          <div className="text-center max-w-2xl mx-auto">
            <img src={logoUrl} alt="Путь рыбы" className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 object-contain" />
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
              Морепродукты напрямую<br />
              <span className="text-sky-500">дешевле вместе</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              Коллективные заказы свежих морепродуктов от проверенных поставщиков с&nbsp;доставкой по&nbsp;Москве и&nbsp;МО
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={onNavigateToCatalog}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-semibold text-white bg-sky-500 hover:bg-sky-600 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-sky-500/25 cursor-pointer"
              >
                Смотреть каталог
                <ArrowRight size={18} />
              </button>
              {!isAuthenticated && (
                <button
                  onClick={() => onNavigate && onNavigate('login')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-2xl transition-colors border border-gray-200 dark:border-gray-700 cursor-pointer"
                >
                  Войти в аккаунт
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Cycle Status Widget */}
      <CycleStatusWidget
        cycleNumber={cycleInfo?.cycle_id}
        cycleName={cycleInfo?.theme_display_name || cycleInfo?.cycle_name}
        status={cycleInfo?.status === 'inactive' ? 'inactive' : cycleInfo?.status === 'closed' ? 'closed' : 'open'}
        deliveryDate={cycleInfo?.delivery_date ? new Date(cycleInfo.delivery_date) : null}
        closeDate={closeDate}
        progress={(() => {
          // Нет цикла — прогресс 0
          if (!cycleInfo || cycleInfo.status === 'inactive') return 0;
          // Персональный статус доставки (этапы 3-4) имеет приоритет
          const deliveryStatus = cycleInfo?.my_order_delivery_status;
          if (deliveryStatus === 'in_transit' || deliveryStatus === 'delivered') return 100;
          if (deliveryStatus === 'packing') return 66;
          // Этапы 1-2: глобальный прогресс цикла
          return products.length > 0 ? Math.max(progress?.percent || 0, 33) : (progress?.percent || 0);
        })()}
        participants={progress?.families || 0}
        targetParticipants={progress?.targetFamilies || 0}
        timerSentinelRef={timerSentinelRef}
      />

      {/* Bonus Program Banner */}
      <div
        className="mx-4 mb-6 xl:max-w-[1200px] xl:mx-auto bg-white/80 dark:bg-gray-800/60 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/40 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg shadow-sm"
        style={{
          WebkitBackdropFilter: 'blur(16px)',
          backdropFilter: 'blur(16px)'
        }}
        onClick={() => onNavigate && onNavigate('profile')}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400/20 to-green-500/10 flex items-center justify-center shrink-0">
            <Gift size={20} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Скидка до 5% на каждый заказ
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Закажите и получите скидку на баланс
            </div>
          </div>
          <ChevronRight size={18} className="text-gray-400 dark:text-gray-500 shrink-0" />
        </div>
      </div>

      {/* Stories Highlights — кружки на всех экранах */}
      <StoriesHighlights stories={stories} products={products} />

      <div className="home-content mx-4 xl:max-w-[1200px] xl:mx-auto bg-white dark:bg-gray-800/50 rounded-2xl shadow-sm mt-4 mb-6 pt-6 pb-3 px-3 md:px-6 overflow-hidden">
        {/* Нет активного цикла — кораблик */}
        {(products.length === 0 || !cycleInfo || cycleInfo.status === 'inactive') && (
          <div className="mx-4 my-8 py-8 px-6 text-center rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(249,115,22,0.04), rgba(14,165,233,0.04))',
              border: '1px solid rgba(14,165,233,0.08)'
            }}
          >
            <img
              src={new URL('../assets/fishing-boat.png', import.meta.url).href}
              alt="Ушли на поиски лучших даров моря"
              className="mx-auto mb-3"
              style={{ maxWidth: '180px', width: '100%' }}
            />
            <div className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">
              Ушли на поиски лучших даров моря
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed max-w-xs mx-auto">
              Скоро откроем новый сбор!
            </div>
          </div>
        )}

        {/* Наш улов — превью товаров текущего сбора (только при активном цикле) */}
        {cycleInfo?.status && cycleInfo.status !== 'inactive' && currentlyOrderedProducts.length > 0 && (
          <section className="horizontal-scroll-section-hybrid">
            <div className="section-header-hybrid">
              <h2 className="section-title-hybrid text-gray-900 dark:text-white">
                <FishSymbol className="section-icon" size={24} strokeWidth={2} />
                Наш улов
              </h2>
              <button
                className="see-all-btn"
                onClick={onNavigateToCatalog}
              >
                Все товары →
              </button>
            </div>
            <div className="scroll-wrapper">
              <div className="scroll-container-hybrid" ref={scrollRef}>
                <div className="scroll-content-hybrid">
                  {currentlyOrderedProducts.map((product, index) => {
                    const transformed = transformProduct(product);
                    const cartItem = cartItems.find(ci => ci.id === transformed.id);
                    return (
                      <ProductCardHybrid
                        key={product.product_id}
                        product={transformed}
                        onAddToCart={handleAddToCartClick}
                        onUpdateQuantity={onUpdateQuantity}
                        onRemoveFromCart={onRemoveFromCart}
                        cartQuantity={cartItem?.quantity || 0}
                        onViewDetails={handleViewDetails}
                        isLast={index === currentlyOrderedProducts.length - 1}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Desktop навигация (стрелки) */}
              <button
                className="nav-arrow nav-left"
                onClick={() => {
                  const container = scrollRef.current;
                  if (container) {
                    const scrollAmount = container.offsetWidth * 0.75;
                    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                  }
                }}
                aria-label="Предыдущие товары"
              >
                <div className="nav-arrow-icon">
                  <svg viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </div>
                <div className="nav-arrow-glass" />
              </button>
              <button
                className="nav-arrow nav-right"
                onClick={() => {
                  const container = scrollRef.current;
                  if (container) {
                    const scrollAmount = container.offsetWidth * 0.75;
                    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                  }
                }}
                aria-label="Следующие товары"
              >
                <div className="nav-arrow-icon">
                  <svg viewBox="0 0 24 24">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
                <div className="nav-arrow-glass" />
              </button>
            </div>
          </section>
        )}
      </div>

      {/* FAQ Section */}
      <div className="border-t border-gray-200/60 dark:border-gray-700/40 mt-4 pt-4">
        <FAQAccordion maxItems={3} showMoreLink={false} />
      </div>
    </div>
  );
};

export default React.memo(HomePage);
